import { useCallback } from 'react';

import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import { getLobDescription } from '~/Utils/lobUtils';

import type { ScopeDimension } from './promotionScope';
import { GLOBAL_SCOPE_LABEL, GLOBAL_SCOPE_VALUE } from './promotionScope';

interface SubOrganizationRef {
  external_id?: string | null;
  name?: string | null;
}

/** Display label for a scope value, shared by the facet filters and the per-change scope rows: the "Global"
 * sentinel reads as "Global"; a LOB renders as its display name and a sub-org as its name (as the sysconfig
 * filters show them), each falling back to the raw key for values outside the local configuration (e.g. a
 * cross-env preview whose members don't exist here). Matching/filtering stays keyed on the raw values —
 * labels apply at render only. */
export const useScopeValueLabel = (): ((dimension: ScopeDimension, value: string) => string) => {
  const { lobConfigurationsDict } = useLobConfiguration();
  // @ts-expect-error TODO: useSysconfig hook needs TypeScript definition
  const { organization } = useSysconfig();
  const subOrganizations: SubOrganizationRef[] = organization?.sub_organizations ?? [];

  return useCallback(
    (dimension: ScopeDimension, value: string): string => {
      if (value === GLOBAL_SCOPE_VALUE) {
        return GLOBAL_SCOPE_LABEL;
      }
      if (dimension === 'lobs') {
        return getLobDescription(value, lobConfigurationsDict) || value;
      }
      return subOrganizations.find((subOrg) => subOrg.external_id === value)?.name || value;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lobConfigurationsDict, organization]
  );
};
